// React Component
import React, { useState, useMemo, useCallback, useLayoutEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowRight, BarChart3, Database, Settings2 } from 'lucide-react'
import homeHeroSwimlane from '../../assets/img/famla-hero-symbol-flow.svg'

// App Component
import { NewProcessOrGroupV2, Tutorial, FloatingTutorialButton, ProcessSkeletonLoader } from '../../components'
import AddProcessOwnerModal from '../modal/addProcessOwnerModal'
import TutorialDataAttributes from '../../components/Tutorial/TutorialDataAttributes'
import { AppLayout, HeaderLayout, ProcessLayoutV2 } from '../../layouts'
import { FamlaServices } from '../../services'
import { listRecentCollectCollections, type RecentCollectCollectionSummary } from '../../services/api/collectDataService'
import { mergeProcessesAndDataCollections } from '../../utils/homeRecentMerge'
import { mergeProcessSearchWithDataCollections } from '../../utils/librarySearchMerge'
import { resolveCollectCardOwnerLabel } from '../../utils/collectCardOwnerLabel'
import { role, useAppDispatch, useAppSelector, company } from '../../stores'
import { Routes } from '../../routes'

// Features

//interface
import { iCompany, iProcess } from '../../components/type'
import { useSearch } from '../../utils'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ReportsModal, AssignProcessToFolderModal, EditprocessModal, GenericModal, PublishModal } from '../modal'
import WhatsNewModal from '../../components/WhatsNewModal'
import OutlookPromoModal from '../../components/OutlookPromoModal'
import { useTutorial } from '../../hooks/useTutorial'
import { homeTutorialSteps } from '../../config/tutorialSteps'
import './home-dashboard.css'
import './homepage-redesign.css'
import HomeProcessRow from './HomeProcessRow'
import HomeDataCollectRow from './HomeDataCollectRow'
import CreateDiagramModal from './CreateDiagramModal'
import { trackUiButtonEventAsync, UI_BUTTON_EVENTS } from '../../services/api/uiButtonStatsService'
/** Affiche le temps gagné à partir du total exact en minutes (somme API, sans arrondi à l’heure). */
function renderExactTimeSaved(
    totalMinutes: number,
    t: (key: string, options?: { defaultValue?: string }) => string
): React.ReactNode {
    const n = Math.max(0, Math.floor(Number.isFinite(totalMinutes) ? totalMinutes : 0));
    const sufH = t('views.processView.homeTimeSavedSuffixHour', { defaultValue: 'h' });
    const sufM = t('views.processView.homeTimeSavedSuffixMinute', { defaultValue: 'm' });
    if (n === 0) {
        return (
            <>
                <span>0</span>
                <span className="home-stat-unit">{sufM}</span>
            </>
        );
    }
    if (n < 60) {
        return (
            <>
                <span>{n}</span>
                <span className="home-stat-unit">{sufM}</span>
            </>
        );
    }
    const h = Math.floor(n / 60);
    const mm = n % 60;
    if (mm === 0) {
        return (
            <>
                <span>{h}</span>
                <span className="home-stat-unit">{sufH}</span>
            </>
        );
    }
    return (
        <span className="home-stat-duration">
            <span className="home-stat-duration-seg">
                <span>{h}</span>
                <span className="home-stat-unit">{sufH}</span>
            </span>
            <span className="home-stat-duration-seg">
                <span>{mm}</span>
                <span className="home-stat-unit">{sufM}</span>
            </span>
        </span>
    );
}

interface iState {
    isLoadingProcess: boolean,
    isLoadingProject: boolean,
    isLoadingPopular: boolean,
    isLoadingApproval: boolean
    isLoading: boolean
    process: any
    myRole: string
    error: string
    popularUseCase: any
    requestApproval: Array<any>
    companyById: iCompany
}


const Home = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const language = localStorage.getItem('currentLanguage') === "fr" ? "french" : "english"
    const users = useAppSelector((state) => state.auth);
    const companyId = users.session.company_id;
    const navigate = useNavigate();

    // Tutorial setup
    const tutorialSteps = homeTutorialSteps(t);
    const {
        run: runTutorial,
        stepIndex,
        steps,
        startTutorial,
        handleJoyrideCallback,
    } = useTutorial(tutorialSteps, 'home', true); // Auto-start for new users
    const [notifProcess, setNotifProcess] = useState<{ [key: string]: boolean }>({});
    const [process, setProcess] = React.useState<iProcess[]>([]);
    const [dataCollections, setDataCollections] = React.useState<RecentCollectCollectionSummary[]>([]);
    /** Suppression processus depuis la ligne récente : modale montée à la demande uniquement. */
    const [homeProcessDeleteId, setHomeProcessDeleteId] = React.useState<string | null>(null);
    const [state, setState] = React.useState<iState>({
        isLoadingProcess: true,
        isLoadingProject: true,
        isLoadingPopular: true,
        isLoadingApproval: true,
        isLoading: true,
        process: [],
        myRole: "WORKER",
        error: '',
        popularUseCase: [],
        requestApproval: [],
        companyById: {}
    })

    const [timeSavedMinutes, setTimeSavedMinutes] = React.useState<number>(0);
    const [isLoadingTimeSaved, setIsLoadingTimeSaved] = React.useState<boolean>(true);
    const [totalInterviews, setTotalInterviews] = React.useState<number | null>(null);
    const [isLoadingInterviewCount, setIsLoadingInterviewCount] = React.useState<boolean>(true);
    const [createDiagramOpen, setCreateDiagramOpen] = React.useState(false);
    const [createDiagramInitialMethod, setCreateDiagramInitialMethod] =
        React.useState<'interview' | 'upload' | 'draw'>('interview');
    const [searchParams, setSearchParams] = useSearchParams();

    React.useEffect(() => {
        const requestedMethod = searchParams.get('createDiagram');
        if (!requestedMethod || !['interview', 'upload', 'draw'].includes(requestedMethod)) return;

        setCreateDiagramInitialMethod(requestedMethod as 'interview' | 'upload' | 'draw');
        setCreateDiagramOpen(true);
        const next = new URLSearchParams(searchParams);
        next.delete('createDiagram');
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);

    const firstName =
        (users.user.fullname || '').trim().split(/\s+/)[0] ||
        t('views.processView.homeGreetingFallback');
    const hour = new Date().getHours();
    const greetingKey =
        hour < 12
            ? 'views.processView.homeGreetingMorning'
            : hour < 18
              ? 'views.processView.homeGreetingAfternoon'
              : 'views.processView.homeGreetingEvening';

    const { searchQuery, setSearchQuery, searchResults, searchLoad, setFilter, filter } = useSearch("/process/search/my/process");
    const safeSearchResults = searchResults ?? [];

    const loadDataCollections = useCallback(async () => {
        if (!users.access_token) return;
        try {
            const r = await listRecentCollectCollections(users.access_token, 60);
            if (r.status === 200 && Array.isArray(r.body?.data?.collections)) {
                setDataCollections(r.body.data.collections as RecentCollectCollectionSummary[]);
                return;
            }
            setDataCollections([]);
        } catch (error) {
            // Évite le crash Runtime Overlay quand l'API /collections/recent est inaccessible.
            console.warn('Home: unable to load recent data collections', error);
            setDataCollections([]);
        }
        // `companyId` : recharge la liste après un basculement de compagnie.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [users.access_token, companyId]);

    const mergedHomeRecents = useMemo(
        () => mergeProcessesAndDataCollections(process, dataCollections),
        [process, dataCollections],
    );

    const mergedSearchResults = useMemo(
        () =>
            searchQuery
                ? mergeProcessSearchWithDataCollections(
                      safeSearchResults as iProcess[],
                      dataCollections,
                      searchQuery,
                      filter,
                  )
                : [],
        [safeSearchResults, dataCollections, searchQuery, filter],
    );

    useLayoutEffect(() => {
        const Modal = (window as any).bootstrap?.Modal;
        document.querySelectorAll('.modal.show').forEach((node) => {
            const inst = Modal?.getInstance(node);
            try {
                inst?.hide();
            } catch {
                /* ignore */
            }
        });
        document.querySelectorAll('.modal-backdrop').forEach((b) => b.remove());
        document.body.classList.remove('modal-open');
        document.body.style.removeProperty('overflow');
        document.body.style.removeProperty('padding-right');
    }, []);


    const loadNotificationByProcess = async (processId: string) => {
        try {
            let data = await FamlaServices("api_host", `/notification/by/id/${processId}`, "GET", "", users.access_token);
            if (data.status === 200) {
                return data.body.data;
            } else {
                return false;
            }
        } catch (e) {
            return false;
        }
    };


    React.useEffect(() => {


        const loadCompanyById = async () => {

            try {
                let data = await FamlaServices("api_host", `/company/by/id/${companyId}`, "GET", "", users.access_token);

                if (data.status === 200) {
                    const companie = data.body.data
                    setState((prev) => ({ ...prev, companyById: companie }));
                    let newData = await FamlaServices("api_host", `/request/total_tokens/company/?company_id=${companyId}`, "GET", "", users.access_token);
                    if (newData.status === 200) {
                        const tokenData = newData.body.data
                        const companyData = {
                            ...companie,
                            tokensRemaining: tokenData.left_token,
                            tokensUsed: tokenData.total_tokens,
                            totalTokens: tokenData.total_tokens

                        }
                        dispatch(company({ company: companyData }));
                    }

                } else {

                }

            } catch (error) {

            }

        }

        loadCompanyById()

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId, users.access_token])




    React.useEffect(() => {
        const updateLang = async () => {
            try {
                const updateData = {
                    fullname: users.user.fullname,
                    phone_number: users.user.phone_number,
                    lang: language,
                    updated_at: users.user.created_at
                };

                let data = await FamlaServices("api_host", `/users/`, "PUT", updateData, users.access_token);
                if (data.status === 200) {
                }
            } catch (error) {

            }

        }
        updateLang()

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [users.access_token, language])


    React.useEffect(() => {
        const loadTimeSaved = async () => {
            try {
                setIsLoadingTimeSaved(true);
                let data = await FamlaServices("api_host", `/stats/company/time-saved`, "GET", "", users.access_token);
                if (data.status === 200 && data.body.data) {
                    const raw = data.body.data.time_saved_minutes;
                    const mins =
                        typeof raw === 'number' && Number.isFinite(raw)
                            ? raw
                            : Math.round((data.body.data.time_saved_hours || 0) * 60);
                    setTimeSavedMinutes(mins);
                }
            } catch (error) {
                console.error('Error loading time saved:', error);
            } finally {
                setIsLoadingTimeSaved(false);
            }
        }

        loadTimeSaved();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [users.access_token, companyId])

    React.useEffect(() => {
        const loadInterviewCount = async () => {
            try {
                setIsLoadingInterviewCount(true);
                const data = await FamlaServices(
                    'api_host',
                    `/process/my/interview-count`,
                    'GET',
                    '',
                    users.access_token
                );
                if (data.status === 200 && data.body?.data != null && typeof data.body.data.total === 'number') {
                    setTotalInterviews(data.body.data.total);
                } else {
                    setTotalInterviews(0);
                }
            } catch {
                setTotalInterviews(0);
            } finally {
                setIsLoadingInterviewCount(false);
            }
        };
        loadInterviewCount();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [users.access_token, companyId]);

    React.useEffect(() => {
        let cancelled = false;

        // Basculement de compagnie : on repart d'un état propre (skeletons) et on
        // remplace les listes plutôt que de fusionner avec l'ancienne compagnie.
        setState((prev) => ({ ...prev, isLoadingProcess: true, isLoadingApproval: true }));
        setProcess([]);
        setNotifProcess({});

        const getMyRole = async () => {
            try {
                const data = await FamlaServices("api_host", `/users/my/role`, "GET", "", users.access_token);
                if (!cancelled && data.status === 200) {
                    setState((prev) => ({ ...prev, myRole: data.body.data }))
                    dispatch(role({ role: data.body.data }))
                }
            } catch (error) {
                /* ignore */
            }
        }

        const getProcessUser = async () => {
            try {
                const data = await FamlaServices("api_host", `/process/my`, "GET", "", users.access_token);
                if (cancelled || data.status !== 200) return;

                const processes: iProcess[] = data.body.data ?? [];

                // Affichage immédiat des cartes (fin du skeleton) sans attendre les notifications.
                setProcess(processes);
                setState((prev) => ({ ...prev, isLoadingProcess: false }));

                // Notifications des 3 premiers processus en parallèle (au lieu d'une cascade).
                const top = processes.slice(0, 3);
                const results = await Promise.all(
                    top.map(async (p) => [p._id, await loadNotificationByProcess(p._id)] as const),
                );
                if (cancelled) return;
                setNotifProcess(Object.fromEntries(results));
            } catch (error) {
                if (!cancelled) setState((prev) => ({ ...prev, isLoadingProcess: false }));
            }
        };

        const loadApprovalList = async () => {
            try {
                const data = await FamlaServices("api_host", `/approval/`, "GET", "", users.access_token);
                if (cancelled) return;
                if (data.status === 200) {
                    setState((prev) => ({ ...prev, requestApproval: data.body.data, isLoadingApproval: false }));
                } else {
                    setState((prev) => ({ ...prev, isLoadingApproval: false, error: 'Failed to fetch data' }));
                }
            } catch (error: any) {
                if (!cancelled) setState((prev) => ({ ...prev, isLoadingApproval: false }));
            }
        }

        // Les trois requêtes indépendantes partent en parallèle.
        getMyRole();
        getProcessUser();
        loadApprovalList();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [users.access_token, companyId])

    React.useEffect(() => {
        // Vide la liste avant de recharger (mount / basculement de compagnie) pour éviter
        // d'afficher les collectes de l'ancienne compagnie. Les mises à jour ponctuelles
        // (onCollectionUpdated) appellent loadDataCollections directement et ne passent pas ici.
        setDataCollections([]);
        void loadDataCollections();
    }, [loadDataCollections])

    const deleProcess = async (process_id: string, name: string): Promise<boolean> => {
        try {
            setProcess(prevState =>
                prevState.filter((process: { _id: string }) => process._id !== process_id)
            );
            const response = await FamlaServices("api_host", `/process/${process_id}`, "DELETE", "", users.access_token);
            return response.status === 200;
        } catch (error) {
            console.error('Error deleting process:', error);
            return false;
        }
    }
    const updateProcess = (newData: any) => {
        setProcess(prev =>
            prev.map(item =>
                item._id === newData._id
                    ? {
                        ...item,
                        title: newData.title,
                        description: newData.description,
                        updated_at: newData.updated_at,
                    }
                    : item
            )
        );
    };

    const openCreateDiagram = useCallback(async () => {
        await trackUiButtonEventAsync(UI_BUTTON_EVENTS.home.quickCreateAi);
        setCreateDiagramInitialMethod('interview');
        setCreateDiagramOpen(true);
    }, []);

    return (
        <AppLayout className="home-app-layout" fillViewport={true}>
            <>
                <div className="home-layout-root">
                <HeaderLayout
                    loadcompany={true}
                    listApproval={state.requestApproval}
                    loading={state.isLoadingApproval}
                    companyById={state.companyById}
                    key={'processView'}
                    inputValue={searchQuery}
                    setSearchQuery={setSearchQuery}
                    setFilter={setFilter}
                    search={true}
                    filterValue={filter}
                    searchfilter={true}
                />

                <main className="home-page-content">
                    {(searchQuery !== '') ? (
                        <ProcessLayoutV2
                                    title={`${t('words.searching')}...${searchQuery}`}
                                    arrowBack={false}
                                    style={{}}
                                    classBlockHeader="justify-content-center"
                                    classNameWrapper="justify-content-center"
                                >
                                    <>
                                        {mergedSearchResults.slice(0, 8).map((entry) =>
                                            entry.kind === 'process' ? (
                                                <HomeProcessRow
                                                    key={`search-p-${entry.item._id}`}
                                                    process={entry.item}
                                                    ownerLabel={
                                                        users.user.fullname === entry.item.owner.fullname
                                                            ? t('words.me')
                                                            : entry.item.owner.fullname
                                                    }
                                                    isOwnerMe={users.user.fullname === entry.item.owner.fullname}
                                                    hasNotif={notifProcess[entry.item._id] || false}
                                                    deleteProcess={() => deleProcess(entry.item._id, entry.item.title)}
                                                    setProcess={updateProcess}
                                                />
                                            ) : (
                                                <HomeDataCollectRow
                                                    key={`search-c-${entry.item.id}`}
                                                    collection={entry.item}
                                                    ownerLabel={resolveCollectCardOwnerLabel(entry.item, t, {
                                                        userId: users.user._id || users.session.user._id,
                                                        userFullname: users.user.fullname,
                                                    })}
                                                    onCollectionUpdated={() => void loadDataCollections()}
                                                />
                                            ),
                                        )}
                                        {!searchLoad && mergedSearchResults.length === 0 && (
                                            <NewProcessOrGroupV2
                                                newElement={t('views.processView.processNotFound')}
                                                create="process"
                                                noResult={true}
                                            />
                                        )}
                                    </>
                                </ProcessLayoutV2>
                    ) : (
                        <div className="home-page-body fh-home">
                                <h1 className="tutorial-welcome visually-hidden">{t('views.processView.startJourneyPrompt')}</h1>

                                <section className="fh-intro" aria-labelledby="fh-greeting">
                                    <div>
                                        <h2 id="fh-greeting">
                                            {t(greetingKey)}, {firstName}
                                        </h2>
                                        <p>{t('views.processView.homeWelcomeSubtitle')}</p>
                                    </div>
                                    <div className="fh-inline-stats" aria-label={t('views.processView.homeStatsMapped')}>
                                        <div>
                                            <strong>{isLoadingTimeSaved ? '—' : renderExactTimeSaved(timeSavedMinutes, t)}</strong>
                                            <span>{t('views.processView.homeStatsTimeSavedShort')}</span>
                                        </div>
                                        <div>
                                            <strong>{state.isLoadingProcess ? '—' : process.length}</strong>
                                            <span>{t('views.processView.homeStatsMapped')}</span>
                                        </div>
                                        <div>
                                            <strong>{isLoadingInterviewCount ? '—' : totalInterviews ?? 0}</strong>
                                            <span>{t('views.processView.homeStatsInterviews')}</span>
                                        </div>
                                    </div>
                                </section>

                                <section
                                    className="fh-hero"
                                    data-tutorial="discover-process"
                                    role="button"
                                    tabIndex={0}
                                    aria-label={t('views.processView.homeCreateDiagram')}
                                    onClick={() => void openCreateDiagram()}
                                    onKeyDown={(e) => {
                                        if (e.key !== 'Enter' && e.key !== ' ') return;
                                        e.preventDefault();
                                        void openCreateDiagram();
                                    }}
                                >
                                    <div className="fh-hero-copy">
                                        <span className="fh-eyebrow">{t('views.processView.homeBuildDiagram')}</span>
                                        <h2>{t('views.processView.homeHeroTitle')}</h2>
                                        <p>{t('views.processView.homeHeroDescription')}</p>
                                        <div className="fh-hero-actions">
                                            <button
                                                type="button"
                                                className="fh-primary-btn"
                                                data-tutorial="generate-diagram"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    void openCreateDiagram();
                                                }}
                                            >
                                                {t('views.processView.homeCreateDiagram')} <ArrowRight size={18} />
                                            </button>
                                            <span>{t('views.processView.homeMethodsSummary')}</span>
                                        </div>
                                    </div>
                                    <div className="fh-process-flow" aria-hidden="true">
                                        <img
                                            className="fh-hero-art"
                                            src={homeHeroSwimlane}
                                            alt=""
                                            decoding="async"
                                        />
                                    </div>
                                </section>

                                <section className="fh-secondary" aria-labelledby="fh-secondary-title">
                                    <h2 id="fh-secondary-title">{t('views.processView.homeThenReady')}</h2>
                                    <div className="fh-secondary-grid">
                                        <button
                                            type="button"
                                            className="fh-action-card"
                                            data-tutorial="data-collect"
                                            onClick={async () => {
                                                await trackUiButtonEventAsync(UI_BUTTON_EVENTS.home.quickDataCollect);
                                                navigate(Routes.DATA_COLLECT);
                                            }}
                                        >
                                            <span className="fh-action-top">
                                                <span className="fh-action-icon collect"><Database size={19} /></span>
                                                <small>Beta</small>
                                            </span>
                                            <strong>{t('views.processView.homeCollectData')}</strong>
                                            <span>{t('views.processView.homeCollectDataDesc')}</span>
                                        </button>
                                        <button
                                            type="button"
                                            className="fh-action-card"
                                            data-tutorial="analyze-optimize"
                                            onClick={async () => {
                                                await trackUiButtonEventAsync(UI_BUTTON_EVENTS.home.quickCompanyAnalysis);
                                                navigate(Routes.COMPANYANALYSIS);
                                            }}
                                        >
                                            <span className="fh-action-icon insights"><BarChart3 size={19} /></span>
                                            <strong>{t('views.processView.homeGetInsights')}</strong>
                                            <span>{t('views.processView.homeGetInsightsDesc')}</span>
                                        </button>
                                        <div className="fh-action-card disabled" aria-disabled="true" data-tutorial="design-future">
                                            <span className="fh-action-top">
                                                <span className="fh-action-icon automate"><Settings2 size={19} /></span>
                                                <small>{t('views.processView.comingSoon')}</small>
                                            </span>
                                            <strong>{t('views.processView.homeAutomate')}</strong>
                                            <span>{t('views.processView.homeAutomateDesc')}</span>
                                        </div>
                                    </div>
                                </section>

                                {/* Processus récents */}
                                <section className="home-recents-section" data-tutorial="recent-processes">
                                    <div className="home-section-header">
                                        <div className="home-section-title">{t('views.processView.homeRecentProcesses')}</div>
                                        <button
                                            type="button"
                                            className="home-section-link"
                                            onClick={async () => {
                                                await trackUiButtonEventAsync(
                                                    UI_BUTTON_EVENTS.home.myProcesses
                                                );
                                                navigate(Routes.MY_PROCESS);
                                            }}
                                        >
                                            {t('views.processView.viewAll')}
                                        </button>
                                    </div>
                                    {state.isLoadingProcess ? (
                                        <div className="home-recents-grid">
                                            {[1, 2, 3, 4].map((index) => (
                                                <ProcessSkeletonLoader key={index} />
                                            ))}
                                        </div>
                                    ) : (
                                        <>
                                            <div className="home-recents-grid">
                                                {mergedHomeRecents.slice(0, 4).map((entry) =>
                                                    entry.kind === 'process' ? (
                                                        <HomeProcessRow
                                                            key={`p-${entry.item._id}`}
                                                            process={entry.item}
                                                            ownerLabel={
                                                                users.user.fullname === entry.item.owner.fullname
                                                                    ? t('words.me')
                                                                    : entry.item.owner.fullname
                                                            }
                                                            isOwnerMe={users.user.fullname === entry.item.owner.fullname}
                                                            hasNotif={notifProcess[entry.item._id] || false}
                                                            deleteProcess={() => deleProcess(entry.item._id, entry.item.title)}
                                                            setProcess={updateProcess}
                                                            onRequestDelete={() => setHomeProcessDeleteId(entry.item._id)}
                                                        />
                                                    ) : (
                                                        <HomeDataCollectRow
                                                            key={`c-${entry.item.id}`}
                                                            collection={entry.item}
                                                            ownerLabel={resolveCollectCardOwnerLabel(entry.item, t, {
                                                                userId: users.user._id || users.session.user._id,
                                                                userFullname: users.user.fullname,
                                                            })}
                                                            onCollectionUpdated={() => void loadDataCollections()}
                                                        />
                                                    ),
                                                )}
                                            </div>
                                            {mergedHomeRecents.slice(0, 4).map((entry) =>
                                                entry.kind === 'process' ? (
                                                    <React.Fragment key={`modals-${entry.item._id}`}>
                                                        <AddProcessOwnerModal
                                                            processId={entry.item._id}
                                                            modalId={`addProcessOwner${entry.item._id}`}
                                                        />
                                                        <AssignProcessToFolderModal processId={entry.item._id} processName={entry.item.title} />
                                                        <EditprocessModal
                                                            folder={false}
                                                            processId={entry.item._id}
                                                            processName={entry.item.title}
                                                            description={entry.item.description}
                                                            setProject={() => {}}
                                                            setProcess={updateProcess}
                                                        />
                                                        {homeProcessDeleteId === entry.item._id ? (
                                                            <GenericModal
                                                                title={t('words.delete')}
                                                                actionButton={t('words.delete')}
                                                                action={() => deleProcess(entry.item._id, entry.item.title)}
                                                                sentence={t('views.processView.areYouSure')}
                                                                processId={entry.item._id}
                                                                autoShow
                                                                onHidden={() => setHomeProcessDeleteId(null)}
                                                            />
                                                        ) : null}
                                                        <PublishModal processId={entry.item._id} />
                                                    </React.Fragment>
                                                ) : null,
                                            )}
                                        </>
                                    )}
                                </section>
                        </div>
                    )}
                </main>
                </div>

                <ReportsModal />
                <WhatsNewModal />
                <OutlookPromoModal trigger="first_mapping" />
                <CreateDiagramModal
                    open={createDiagramOpen}
                    initialMethod={createDiagramInitialMethod}
                    onClose={() => setCreateDiagramOpen(false)}
                />

                {/* Tutorial Component */}
                <Tutorial
                    run={runTutorial}
                    steps={steps}
                    stepIndex={stepIndex}
                    callback={handleJoyrideCallback}
                    continuous={true}
                    showProgress={true}
                    showSkipButton={true}
                />

                {/* Floating Tutorial Button */}
                <FloatingTutorialButton
                    onClick={async () => {
                        await trackUiButtonEventAsync(UI_BUTTON_EVENTS.home.startTutorial);
                        startTutorial();
                    }}
                />

                {/* Tutorial Data Attributes Injection */}
                <TutorialDataAttributes />
            </>
        </AppLayout>
    )
}

export default Home